import type {
  ContratDTO,
  ContratProADTO,
  EmployeurDTO,
  FormationDTO,
  SalarieDTO,
  TutoratDTO,
} from '@/features/api/extranet/dto/Demande/PROA'
import type { FinancementDTO } from '@/features/api/extranet/dto/Demande/shared/FinancementDTO'
import {
  addPreBuilder,
  targetPreBuilder,
  type Flag,
  type ModelValidateurResult,
  type Regles,
} from '@/features/domaine/moteur-regles'
import { LevelRegleEnum } from '@/features/domaine/types/opco/shared'
import type { ContextReglesProA, ReglesProA } from './types'

import {
  checkNir,
  checkNirDateNaissance,
  checkNirSexe,
  checkNirTemporaire,
  getRangeDate,
  isAdherentOuMandatGestion,
  preDateValide,
  preEstNirPortail,
  preNotNullOrEmpty,
  rgEstCodePostalFactory,
  rgEstCommuneFactory,
  rgEstDate,
  rgEstMailFactory,
  rgEstMaxLengthFactory,
  rgEstNomOuPrenomFactory,
  rgEstNumeric,
  rgEstNumericMaxFactory,
  rgEstNumericMinFactory,
  rgEstNumTelFactory,
  rgEstObligatoireFactory,
  rgEstVoieFactory,
  rgEstVraiFactory,
} from '../shared'

import type { EtablissementAdherentDTO } from '@/features/api/extranet/dto/shared'
import {
  addMonth,
  checkDateInRange,
  getAge,
  getNbJour,
  getToday,
  isDate,
  isNotNullUndefinedOrEmptyString,
  isNullOrUndefined,
  isNumber,
} from '@/features/shared/tools'
import { PropertyStatus } from '../../property-status'
import { SAISIE_INVALIDE, SAISIE_OBLIGATOIRE } from '../shared/messages-erreurs'
import { preNirTemporaire, preNotNirTemporaire } from './preconditions'

/* Initalisation des helpers */
const targetPre = targetPreBuilder<ContratProADTO, ContextReglesProA>()
const addPre = addPreBuilder<ContratProADTO, ContextReglesProA>()

/* Declaration de type pour les sous modeles */
type RegleProAMembre<T extends object> = Regles<T, ContextReglesProA, LevelRegleEnum, Flag, ContratProADTO>

type ReglesProAEmployeur = RegleProAMembre<EmployeurDTO>
type ReglesProAEmployeurEtablissement = RegleProAMembre<EtablissementAdherentDTO>
type ReglesProASalarie = RegleProAMembre<SalarieDTO>
type ReglesProATutorat = RegleProAMembre<TutoratDTO>
type ReglesProAContrat = RegleProAMembre<ContratDTO>
type ReglesProAFormation = RegleProAMembre<FormationDTO>
type ReglesProAFinancement = RegleProAMembre<FinancementDTO>

/* Employeur */
/* Etablissement */
const reglesEtablissementEmployeur: ReglesProAEmployeurEtablissement = {
  adresse: {},
  denomination: {},
  siret: {},
  typeEntreprise: {
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
  },
  idcc: {
    code: {
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.employeur?.etablissement?.idcc?.code?.$status === PropertyStatus.Required,
        rgEstObligatoireFactory(LevelRegleEnum.Technique),
      ),
    },
  },
  naf: {
    code: {
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.employeur?.etablissement?.naf?.code?.$status === PropertyStatus.Required,
        rgEstObligatoireFactory(LevelRegleEnum.Technique),
      ),
    },
  },
}

const reglesEmployeur: ReglesProAEmployeur = {
  contact: {
    email: {
      ...rgEstMaxLengthFactory(200),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.employeur?.contact?.email?.$status === PropertyStatus.Required,
        rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      ),
      ...rgEstMailFactory(LevelRegleEnum.Metier),
    },
    telephone: {
      ...rgEstMaxLengthFactory(10),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.employeur?.contact?.telephone?.$status === PropertyStatus.Required,
        rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      ),
      ...rgEstNumTelFactory(LevelRegleEnum.Metier),
    },
  },
  etablissement: reglesEtablissementEmployeur,
}

/* Salarie */
const reglesSalarie: ReglesProASalarie = {
  nomNaissance: {
    ...rgEstMaxLengthFactory(50),
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
  },
  prenom: {
    ...rgEstMaxLengthFactory(50),
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
  },
  nom: {
    ...rgEstMaxLengthFactory(50),
    ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
  },
  nirTemporaire: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.salarie?.nirTemporaire?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  nir: {
    ...addPre(preNotNirTemporaire, rgEstMaxLengthFactory(13, LevelRegleEnum.Metier)),
    ...rgEstMaxLengthFactory(250),
    ...addPre((_value, _parent, _model, context) => context.status.salarie?.nir?.$status === PropertyStatus.Required, {
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    }),

    'check-nir': {
      pre: [preNotNullOrEmpty, preEstNirPortail, preNotNirTemporaire],
      validateur: (value) => checkNir(value!),
      message: 'Le format du NIR est incorrect',
      level: LevelRegleEnum.Metier,
    },
    /*
    'check-nir-api': {
      pre: [preNotNullOrEmpty, preEstNirPortail, preNotNirTemporaire, (_, parent) => (parent.nomNaissance?.length ?? 0) > 2],
      validateur: async (value, parent, _modele, context) => {
        if (['1', '2'].includes(value![0]) === false) return

        const result = await context.services.validerNir(value!, parent.nomNaissance!)
        return result.isValidFormat === true && result.nirDetails !== null
      },
      message: 'Le NIR ne correspond pas au nom de naissance',
      level: LevelRegleEnum.Metier,
    },
    */
    'check-nir-temporaire': {
      pre: [preNotNullOrEmpty, preEstNirPortail, preNirTemporaire],
      validateur: (value) => checkNirTemporaire(value!),
      message: 'Le format du NIR temporaire est incorrect',
      level: LevelRegleEnum.Metier,
    },
  },
  sexe: {
    ...addPre((_value, _parent, _model, context) => context.status.salarie?.sexe?.$status === PropertyStatus.Required, {
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    }),
    controleSexeNir: {
      pre: [preNotNullOrEmpty, targetPre('salarie.nir', preNotNullOrEmpty), preNotNirTemporaire],
      validateur: (value, parent) => checkNirSexe(parent?.nir ?? '', value!),
      message: "Le sexe de l'apprenti ne correspond pas au NIR saisi",
      level: LevelRegleEnum.Metier,
    },
  },
  dateNaissance: {
    ...rgEstDate,
    ...addPre(
      (_value, _parent, _model, context) => context.status.salarie?.dateNaissance?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    controleDateNaissanceNir: {
      pre: [preNotNullOrEmpty, targetPre('salarie.nir', preNotNullOrEmpty), preNotNirTemporaire],
      validateur: (value, parent) => checkNirDateNaissance(parent?.nir ?? '', new Date(value!)),
      message: "La date de naissance de l'apprenti ne correspond pas au NIR saisi",
      level: LevelRegleEnum.Metier,
    },
  },
  beneficieReconnaissanceTravailleurHandicape: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.salarie?.beneficieReconnaissanceTravailleurHandicape?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  csp: {
    ...addPre((_value, _parent, _model, context) => context.status.salarie?.csp?.$status === PropertyStatus.Required, {
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    }),
  },
  inscritPoleEmploi: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.salarie?.inscritPoleEmploi?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  situation: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.salarie?.situation?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  typeMinimuSocial: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.salarie?.typeMinimuSocial?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  diplomeOuTitreLePlusEleveObtenu: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.salarie?.diplomeOuTitreLePlusEleveObtenu?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  adresse: {
    voie: {
      ...rgEstMaxLengthFactory(38),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.salarie?.adresse?.voie?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      ...rgEstVoieFactory(LevelRegleEnum.Metier),
    },
    complement: {
      ...rgEstMaxLengthFactory(38),
    },
    codePostal: {
      ...rgEstMaxLengthFactory(10),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.salarie?.adresse?.codePostal?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      ...rgEstCodePostalFactory(LevelRegleEnum.Metier),
    },
    commune: {
      ...rgEstMaxLengthFactory(50),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.salarie?.adresse?.commune?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      ...rgEstCommuneFactory(LevelRegleEnum.Metier),
    },
  },
  email: {
    ...rgEstMaxLengthFactory(64),
    ...rgEstMailFactory(LevelRegleEnum.Metier),
    ...addPre(
      (_value, _parent, _model, context) => context.status.salarie?.email?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  telephone: {
    ...rgEstMaxLengthFactory(10),
    ...rgEstNumTelFactory(LevelRegleEnum.Metier),
    ...addPre(
      (_value, _parent, _model, context) => context.status.salarie?.telephone?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  statutRefugie: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.salarie?.statutRefugie?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  publicSpecifique: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.salarie?.publicSpecifique?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
}

/* Tutorat */
const reglesTutorat: ReglesProATutorat = {
  attestationTuteurCriteresEligibilite: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.tutorat?.attestationTuteurCriteresEligibilite?.$status === PropertyStatus.Required,
      {
        ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  attestationTuteurTroisStagiaires: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.tutorat?.attestationTuteurTroisStagiaires?.$status === PropertyStatus.Required,
      {
        ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  tuteurEntrepriseEmployeur: {
    nom: {
      ...rgEstMaxLengthFactory(50),
      ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.tuteurEntrepriseEmployeur?.nom?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
    prenom: {
      ...rgEstMaxLengthFactory(50),
      ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.tuteurEntrepriseEmployeur?.prenom?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
    dateNaissance: {
      ...rgEstDate,
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.tuteurEntrepriseEmployeur?.dateNaissance?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      controle18Ans: {
        pre: [preNotNullOrEmpty],
        validateur: (value, _parent, _modele) => {
          const age = getAge(new Date(value!), getToday())
          return age >= 18
        },
        message: 'Le tuteur doit être majeur',
        level: LevelRegleEnum.Metier,
      },
    },
    souhaiteAideFonctionTutorale: {
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.tuteurEntrepriseEmployeur?.souhaiteAideFonctionTutorale?.$status ===
          PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
  },
  tuteurEntrepriseUtilisatrice: {
    nom: {
      ...rgEstMaxLengthFactory(50),
      ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.tuteurEntrepriseUtilisatrice?.nom?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
    prenom: {
      ...rgEstMaxLengthFactory(50),
      ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.tuteurEntrepriseUtilisatrice?.prenom?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
    dateNaissance: {
      ...rgEstDate,
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.tuteurEntrepriseUtilisatrice?.dateNaissance?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      controle18Ans: {
        pre: [preNotNullOrEmpty],
        validateur: (value, _parent, _modele) => {
          const age = getAge(new Date(value!), getToday())
          return age >= 18
        },
        message: 'Le tuteur doit être majeur',
        level: LevelRegleEnum.Metier,
      },
    },
  },
}

/* Contrat */
const reglesContrat: ReglesProAContrat = {
  emploiOccupe: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.contrat?.emploiOccupe?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  emploiCcn: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.contrat?.emploiCcn?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  niveauQualificationCcn: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.contrat?.niveauQualificationCcn?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    ...rgEstMaxLengthFactory(15, LevelRegleEnum.Metier),
  },
  coefficientCcn: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.contrat?.coefficientCcn?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  dateDebutExecutionContrat: {
    ...rgEstDate,
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.contrat?.dateDebutExecutionContrat?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  dureeHebdomadaire: {
    heure: {
      ...rgEstNumeric,
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.contrat?.dureeHebdomadaire?.heure?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      format: {
        pre: [preNotNullOrEmpty],
        validateur: (_value, parent) => {
          return !isNullOrUndefined(parent.minute)
        },
        message: SAISIE_INVALIDE,
        level: LevelRegleEnum.Technique,
      },

      controleMaximum: {
        pre: [preNotNullOrEmpty],
        validateur: (value, parent) => {
          const duree: number = Number(value) + (parent.minute ?? 0.0) / 100
          return duree <= 35
        },
        message: 'La durée hebdomadaire de travail ne peut excéder 35 h',
        level: LevelRegleEnum.Metier,
      },
    },
  },
  proaVise: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.contrat?.proaVise?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  emploiVise: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.contrat?.emploiVise?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },

  acquisVae: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.contrat?.acquisVae?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  effectueeSurLeTempsDeTravail: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.contrat?.effectueeSurLeTempsDeTravail?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  emploiCcnVise: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.contrat?.emploiCcnVise?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  niveauQualificationCcnVise: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.contrat?.niveauQualificationCcnVise?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    ...rgEstMaxLengthFactory(15, LevelRegleEnum.Metier),
  },

  dateDebutExecutionProA: {
    ...rgEstDate,
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.contrat?.dateDebutExecutionProA?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    controleRange: {
      pre: [preNotNullOrEmpty, targetPre('contrat.dateFinExecutionProA', preDateValide)],
      validateur: (value, _parent, modele) =>
        checkDateInRange(new Date(value!), getRangeDate(null, new Date(modele.contrat!.dateFinExecutionProA!))),
      message: 'La date de début doit être inférieure à la date de fin',
      level: LevelRegleEnum.Metier,
    },
  },
  dateFinExecutionProA: {
    ...rgEstDate,
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.contrat?.dateFinExecutionProA?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),

    controleRange: {
      pre: [preNotNullOrEmpty, targetPre('contrat.dateDebutExecutionProA', preDateValide)],
      validateur: (value, _parent, modele) =>
        checkDateInRange(new Date(value!), getRangeDate(new Date(modele.contrat!.dateDebutExecutionProA!), null)),
      message: 'La date de fin doit être supérieure à la date de début',
      level: LevelRegleEnum.Metier,
    },
  },
}

/* Formation */
const reglesFormation: ReglesProAFormation = {
  intitule: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.formation?.intitule?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  nsf: {
    code: {
      ...addPre(
        (_value, _parent, _model, context) => context.status.formation?.nsf?.code?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
  },

  certificationVisee: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.formation?.certificationVisee?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },

  typeQualification: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.formation?.typeQualification?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },

  dureeTotale: {
    ...rgEstNumeric,
    ...addPre(
      (_value, _parent, _model, context) => context.status.formation?.dureeTotale?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    ...addPre(
      (_value, _parent, model) => model.contrat?.acquisVae !== true && model.formation?.typeQualification !== 'Clea',
      rgEstNumericMinFactory(149, LevelRegleEnum.Technique, 'La durée de formation doit être supérieure ou égale à 150 heures'),
    ),
    controleQuotite: {
      pre: [
        preNotNullOrEmpty,
        (_value, _parent, model) => model.contrat?.acquisVae !== true && model.formation?.typeQualification !== 'Clea',
        targetPre('contrat.dateDebutExecutionProA', preDateValide),
        targetPre('contrat.dateFinExecutionProA', preDateValide),
        targetPre('contrat.dureeHebdomadaire.heure', preNotNullOrEmpty),
      ],
      validateur: (value, _parent, model) => {
        const nbJour =
          getNbJour(new Date(model.contrat!.dateDebutExecutionProA!), new Date(model.contrat!.dateFinExecutionProA!)) +
          1
        const duree: number =
          Number(model.contrat?.dureeHebdomadaire?.heure) + (model.contrat?.dureeHebdomadaire?.minute ?? 0.0) / 100
        const quotite = (value! / ((((1607 * nbJour) / 365) * duree) / 35)) * 100

        return quotite >= 15 && quotite <= 55
      },
      message: 'La durée de formation doit être comprise en 15 et 55 % de la durée totale du contrat.',
      level: LevelRegleEnum.Metier,
    },
  },
  dureeEnseignementsGenerauxEtTechnologique: {
    ...rgEstNumeric,
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.formation?.dureeEnseignementsGenerauxEtTechnologique?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    ...rgEstNumericMinFactory(-1, LevelRegleEnum.Technique, 'La durée doit être supérieure à 0 heure'),
    controleMaximum: {
      pre: [preNotNullOrEmpty],
      validateur: (value, parent, _model) => {
        if (isNullOrUndefined(parent.dureeTotale)) return true

        const duree: number = Number(parent.dureeTotale)

        return Number(value) <= duree
      },
      message: 'La durée de ces enseignements ne peut pas être supérieure à la durée totale des actions.',
      level: LevelRegleEnum.Metier,
    },
  },
  modules: {
    dateDebut: {
      ...rgEstDate,
      controleDateFinFormation: {
        pre: [preNotNullOrEmpty],
        validateur: (value, parent, _modele) => {
          if (!isDate(parent.dateFin)) return true

          return checkDateInRange(new Date(value!), getRangeDate(null, new Date(parent.dateFin!)))
        },
        message: 'La date de début est supérieure à la date de fin',
        level: LevelRegleEnum.Metier,
      },
      controleDateDebutContrat: {
        pre: [
          preNotNullOrEmpty,
          targetPre('contrat.dateDebutExecutionProA', preDateValide),
          targetPre('contrat.dateFinExecutionProA', preDateValide),
        ],
        validateur: (value, _parent, model) => {
          const dateDebutFormation: Date = new Date(value!)
          const dateDebutContrat: Date = new Date(model.contrat!.dateDebutExecutionProA!)
          const dateFinContrat: Date = new Date(model.contrat!.dateFinExecutionProA!)
          return checkDateInRange(dateDebutFormation, getRangeDate(dateDebutContrat, dateFinContrat))
        },
        message: 'La date de début de formation doit avoir lieu pendant la durée du contrat',
        level: LevelRegleEnum.Metier,
      },
      /*
      controleDateDebutContratPlus2MoisDebutFormation: {
        pre: [preNotNullOrEmpty, targetPre('contrat.dateDebutExecutionProA', preDateValide)],
        validateur: (value, _parent, model) => {
          const dateDebutFormation: Date = new Date(value!)
          const dateDebutContrat: Date = new Date(model.contrat?.dateDebutExecutionProA!)
          const dateDebutContratMax: Date = addMonth(dateDebutContrat, 2)

          return checkDateInRange(dateDebutFormation, getRangeDate(null, dateDebutContratMax))
        },
        message: 'La date de début de contrat doit être maximum 2 mois avant la date de début de formation',
        level: LevelRegleEnum.Metier,
      },
      */
    },
    dateFin: {
      ...rgEstDate,
      controleDateDebutFormation: {
        pre: [preNotNullOrEmpty],
        validateur: (value, parent, _modele) => {
          if (!isDate(parent.dateDebut)) return true

          return checkDateInRange(new Date(value!), getRangeDate(new Date(parent.dateFin!), null))
        },
        message: 'La date de fin est inférieur à la date de fin',
        level: LevelRegleEnum.Metier,
      },
      /*
      controleDateFinFormation: {
        pre: [
          preNotNullOrEmpty,
          targetPre('contrat.dateFinExecutionProA', preDateValide),
          targetPre('contrat.dateDebutExecutionProA', preDateValide),
        ],
        validateur: (value, _parent, model) => {
          return checkDateInRange(
            new Date(value!),
            getRangeDate(
              new Date(model.contrat?.dateDebutExecutionProA!),
              new Date(model.contrat?.dateFinExecutionProA!),
            ),
          )
        },
        message: 'La date de fin de formation doit être durant la période de contrat',
        level: LevelRegleEnum.Metier,
      },*/
    },
    duree: {
      ...rgEstNumeric,
    },
    dureeFoad: {
      ...rgEstNumeric,
      controleMaximum: {
        pre: [preNotNullOrEmpty],
        validateur: (value, parent, _modele) => {
          if (isNumber(parent.duree) || parent.modalitePedagogique !== 'Mixte') return true
          const duree = Number(parent.duree)
          const dureeFoad = Number(value)
          return duree > dureeFoad
        },
        message: 'la durée FOAD ne peut pas être égale ou supérieure à la durée totale de la formation.',
        level: LevelRegleEnum.Metier,
      },
    },
    fraisAnnexesSalarie: {
      quantite: {
        ...rgEstNumeric,
        ...rgEstNumericMinFactory(0, LevelRegleEnum.Technique, 'La quantité doit être supérieure à 0'),
        ...rgEstNumericMaxFactory(1000, LevelRegleEnum.Technique, 'La quantité doit être inférieure à 1000'),
      },
      montant: {
        ...rgEstNumeric,
        ...rgEstNumericMinFactory(0, LevelRegleEnum.Technique, 'Le montant doit être supérieur à 0'),
        ...rgEstNumericMaxFactory(100000, LevelRegleEnum.Technique, 'Le montant doit être inférieur à 100000'),
      },
    },
    fraisPedagogiques: {
      montant: {
        ...rgEstNumeric,
        ...rgEstNumericMinFactory(0, LevelRegleEnum.Technique, 'Le montant doit être supérieur à 0'),
        ...rgEstNumericMaxFactory(100000, LevelRegleEnum.Technique, 'Le montant doit être inférieur à 100000'),
      },
    },
  },
}

/* Financement */
const reglesFinancement: ReglesProAFinancement = {
  attestationResteChargeContribution: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.financement?.attestationResteChargeContribution?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  modePriseEnChargeReste: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.financement?.modePriseEnChargeReste?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Metier),
      },
    ),
  },
}

/* REGLES  ------------------------------------------------------------------------ */
export const reglesConfiguration: ReglesProA = {
  validationNotice: {
    ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
  },
  attestationSurHonneur: {
    ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
  },
  attestationPiecesJustificatives: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.attestationPiecesJustificatives?.$status === PropertyStatus.Required,
      {
        ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  acceptationCg: {
    ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
  },
  employeur: reglesEmployeur,
  salarie: reglesSalarie,
  tutorat: reglesTutorat,
  contrat: reglesContrat,
  formation: reglesFormation,
  financement: reglesFinancement,
  lieuSignature: {
    ...addPre((_value, _parent, _model, context) => context.status.lieuSignature?.$status === PropertyStatus.Required, {
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    }),
  },
  dateSignature: {
    ...rgEstDate,
    ...addPre((_value, _parent, _model, context) => context.status.dateSignature?.$status === PropertyStatus.Required, {
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    }),
  },
  $model: {
    controle15ansDateConclusion: {
      pre: (model) => {
        return preDateValide(model.contrat?.dateDebutExecutionProA) && preDateValide(model.salarie?.dateNaissance)
      },
      validateur: (model) => {
        const age = getAge(new Date(model.salarie!.dateNaissance!), new Date(model.contrat!.dateDebutExecutionProA!))
        // console.log(age)
        if (age < 16)
          return {
            'contrat.dateDebutExecutionProA': false,
            'salarie.dateNaissance': false,
          }
      },
      message: "L'âge du salarié doit être supérieur ou égal à 16 ans à la date de début d’exécution de la Pro-A",
      level: LevelRegleEnum.Metier,
    },

    controleObligatoireModules: {
      validateur: (model, _gModel, context) => {
        const result: ModelValidateurResult<ContratProADTO> = {}

        for (const [index, prop] of model.formation!.modules!.entries()) {
          if (
            context.status.formation?.modules?.at(index)?.etablissementPrestataire?.siret?.valeur?.$status ===
              PropertyStatus.Required &&
            prop.typeModule === 'Externe' &&
            isNullOrUndefined(prop.etablissementPrestataire)
          ) {
            result[`formation.modules.${index}.etablissementPrestataire.siret.valeur`] = SAISIE_OBLIGATOIRE
          }

          if (
            context.status.formation?.modules?.at(index)?.modalitePedagogique?.$status === PropertyStatus.Required &&
            isNullOrUndefined(prop.modalitePedagogique)
          ) {
            result[`formation.modules.${index}.modalitePedagogique`] = SAISIE_OBLIGATOIRE
          }

          if (
            context.status.formation?.modules?.at(index)?.dateDebut?.$status === PropertyStatus.Required &&
            isNullOrUndefined(prop.dateDebut)
          ) {
            result[`formation.modules.${index}.dateDebut`] = SAISIE_OBLIGATOIRE
          }

          if (
            context.status.formation?.modules?.at(index)?.dateFin?.$status === PropertyStatus.Required &&
            isNullOrUndefined(prop.dateFin)
          ) {
            result[`formation.modules.${index}.dateFin`] = SAISIE_OBLIGATOIRE
          }

          if (
            context.status.formation?.modules?.at(index)?.duree?.$status === PropertyStatus.Required &&
            isNullOrUndefined(prop.duree)
          ) {
            result[`formation.modules.${index}.duree`] = SAISIE_OBLIGATOIRE
          }

          if (
            context.status.formation?.modules?.at(index)?.dureeFoad?.$status === PropertyStatus.Required &&
            isNullOrUndefined(prop.dureeFoad)
          ) {
            result[`formation.modules.${index}.dureeFoad`] = SAISIE_OBLIGATOIRE
          }

          if (
            context.status.formation?.modules?.at(index)?.attestationMoyenRealisation?.$status ===
              PropertyStatus.Required &&
            prop.attestationMoyenRealisation !== true
          ) {
            result[`formation.modules.${index}.attestationMoyenRealisation`] = SAISIE_OBLIGATOIRE
          }

          if (
            context.status.formation?.modules?.at(index)?.avecSubrogation?.$status === PropertyStatus.Required &&
            isNullOrUndefined(prop.avecSubrogation)
          ) {
            result[`formation.modules.${index}.avecSubrogation`] = SAISIE_OBLIGATOIRE
          }

          for (const [indexFrais, propFrais] of model.formation!.modules!.at(index)!.fraisAnnexesSalarie!.entries()) {
            if (
              context.status.formation?.modules?.at(index)?.fraisAnnexesSalarie?.at(indexFrais)?.montant?.$status ===
                PropertyStatus.Required &&
              isNullOrUndefined(propFrais.montant)
            ) {
              result[`formation.modules.${index}.fraisAnnexesSalarie.${indexFrais}.montant`] = SAISIE_OBLIGATOIRE
            }

            if (
              context.status.formation?.modules?.at(index)?.fraisAnnexesSalarie?.at(indexFrais)?.quantite?.$status ===
                PropertyStatus.Required &&
              isNullOrUndefined(propFrais.quantite)
            ) {
              result[`formation.modules.${index}.fraisAnnexesSalarie.${indexFrais}.quantite`] = SAISIE_OBLIGATOIRE
            }
          }

          for (const [indexFrais, propFrais] of model.formation!.modules!.at(index)!.fraisPedagogiques!.entries()) {
            if (
              context.status.formation?.modules?.at(index)?.fraisPedagogiques?.at(indexFrais)?.montant?.$status ===
                PropertyStatus.Required &&
              isNullOrUndefined(propFrais.montant)
            ) {
              result[`formation.modules.${index}.fraisPedagogiques.${indexFrais}.montant`] = SAISIE_OBLIGATOIRE
            }

            if (
              context.status.formation?.modules?.at(index)?.fraisPedagogiques?.at(indexFrais)?.quantite?.$status ===
                PropertyStatus.Required &&
              isNullOrUndefined(propFrais.quantite)
            ) {
              result[`formation.modules.${index}.fraisPedagogiques.${indexFrais}.quantite`] = SAISIE_OBLIGATOIRE
            }
          }
        }

        return result
      },
      level: LevelRegleEnum.Transmettre,
    },
    controleEtablissementPrestataireActif: {
      validateur: (model, _gModel, _context) => {
        const result: ModelValidateurResult<ContratProADTO> = {}
        if (isNullOrUndefined(model.formation) || isNullOrUndefined(model.formation?.modules)) return result

        for (const [index, prop] of model.formation!.modules!.entries()) {
          if (
            prop.typeModule === 'Externe' &&
            isNotNullUndefinedOrEmptyString(prop.etablissementPrestataire?.siret?.valeur) &&
            prop.etablissementPrestataire.etatAdministratif === 'F'
          ) {
            result[`formation.modules.${index}.etablissementPrestataire.siret.valeur`] = false
          }
        }

        return result
      },
      message: 'Vous ne pouvez pas sélectionner un établissement inactif, merci de sélectionner un autre établissement',
      level: LevelRegleEnum.Technique,
    },
    controleDateContratEtFormation: {
      pre: (model) => {
        return (
          preDateValide(model.contrat?.dateDebutExecutionProA) || preDateValide(model.contrat?.dateFinExecutionProA)
        )
      },
      validateur: (model) => {
        const isDateDebutContrat = isDate(model.contrat?.dateDebutExecutionProA)
        const isDateFinContrat = isDate(model.contrat?.dateFinExecutionProA)

        const dateDebutContrat = new Date(model.contrat?.dateDebutExecutionProA ?? 0)
        const dateFinContrat = new Date(model.contrat?.dateFinExecutionProA ?? 0)

        // On récupère la date début max
        const dateDebutFormationMin: string | null | undefined = model.formation?.modules
          ?.filter((x) => isDate(x.dateDebut) == true)
          ?.map((x) => x.dateDebut)
          .reduce((prev, curr) => (prev != null && new Date(prev!) < new Date(curr!) ? prev : curr), null)

        // On récupère la date fin max
        const dateFinFormationMax: string | null | undefined = model.formation?.modules
          ?.filter((x) => isDate(x.dateFin) == true)
          ?.map((x) => x.dateFin)
          .reduce((prev, curr) => (prev != null && new Date(prev!) > new Date(curr!) ? prev : curr), null)

        const result: ModelValidateurResult<ContratProADTO> = {}

        for (const [index, prop] of model.formation!.modules!.entries()) {
          // Contrôle date de début
          if (isDate(prop.dateDebut) && isDateDebutContrat === true) {
            const dateDebutFormation: Date = new Date(prop.dateDebut!)
            const dateDebutContratMax: Date = addMonth(dateDebutContrat, 2)

            if (
              checkDateInRange(dateDebutFormation, getRangeDate(null, dateDebutContratMax)) === false &&
              prop.dateDebut === dateDebutFormationMin
            ) {
              const message = 'La date de début de formation doit être maximum 2 mois après la date de début de contrat'
              result[`formation.modules.${index}.dateDebut`] = message
              result['contrat.dateDebutExecutionProA'] = message
            }
          }

          // Contrôle date de fin
          if (isDate(prop.dateFin) && isDateFinContrat === true) {
            const dateFinFormation: Date = new Date(prop.dateFin!)
            const dateFinContratMin: Date = addMonth(dateFinContrat, -2)

            if (checkDateInRange(dateFinFormation, getRangeDate(null, dateFinContrat)) === false) {
              const message = 'La date de fin de formation doit être inférieure ou égale à la date de fin de contrat.'
              result[`formation.modules.${index}.dateFin`] = message
              result['contrat.dateFinExecutionProA'] = message
            } else if (
              checkDateInRange(dateFinFormation, getRangeDate(dateFinContratMin, null)) === false &&
              prop.dateFin === dateFinFormationMax
            ) {
              const message = 'La date de fin de formation doit être au maximum 2 mois avant la date de fin de contrat'
              result[`formation.modules.${index}.dateFin`] = message
              result['contrat.dateFinExecutionProA'] = message
            }
          }
        }

        return result
      },
      level: LevelRegleEnum.Metier,
    },
    controleDureeModule: {
      pre: (model) => {
        return preNotNullOrEmpty(model.formation?.dureeTotale)
      },
      validateur: (model, _gModel, _context) => {
        const result: ModelValidateurResult<ContratProADTO> = {}

        const nbHeureModules = model.formation?.modules?.reduce((sum, module) => sum + (module.duree ?? 0), 0) ?? 0

        if (nbHeureModules > model.formation!.dureeTotale!) {
          const message = 'La durée totale des modules est supérieure à la durée totale de formation'
          result[`formation.dureeTotale`] = message
          for (const [index, _prop] of model.formation!.modules!.entries()) {
            result[`formation.modules.${index}.duree`] = message
          }
        }

        if (nbHeureModules > 0 && nbHeureModules! !== model.formation!.dureeTotale!) {
          const message = "La durée totale des modules n'est pas égale à la durée totale de formation"
          result[`formation.dureeTotale`] = message
        }

        return result
      },
      level: LevelRegleEnum.Metier,
    },

    controleNbModule: {
      validateur: (model, _gModel, _context) => {
        const result: ModelValidateurResult<ContratProADTO> = {}
        if (model.formation?.modules?.length == 0) {
          result[`formation.modules`] = 'Un module est obligatoire'
        }

        return result
      },
      level: LevelRegleEnum.Transmettre,
    },
    controleFichier: {
      validateur: (model, _globalModel, context) => {
        const result: ModelValidateurResult<ContratProADTO> = {}

        if (isAdherentOuMandatGestion(context.typeContext, false)) {
          for (const doc of context.naturesFichiers.obligatoires) {
            if (model.fichiers?.filter((x) => x.nature === doc).length === 0) {
              result[`fichiers`] = false
            }
          }
        }
        return result
      },
      message: SAISIE_OBLIGATOIRE,
      level: LevelRegleEnum.Transmettre,
    },
    controleCodeRncpRSNsf: {
      validateur: (model, _globalModel, context) => {
        const result: ModelValidateurResult<ContratProADTO> = {}

        if (
          context.status.formation?.rncp?.code?.$status === PropertyStatus.Required &&
          (isNullOrUndefined(model.formation?.rncp) || isNullOrUndefined(model.formation?.rncp?.code))
        )
          result['formation.rncp.code'] = false

        if (
          context.status.formation?.repertoireSpecifique?.code?.$status === PropertyStatus.Required &&
          (isNullOrUndefined(model.formation?.repertoireSpecifique) ||
            isNullOrUndefined(model.formation?.repertoireSpecifique?.code))
        )
          result['formation.repertoireSpecifique.code'] = false

        if (
          context.status.formation?.nsf?.code?.$status === PropertyStatus.Required &&
          (isNullOrUndefined(model.formation?.nsf) || isNullOrUndefined(model.formation?.nsf?.code))
        )
          result['formation.nsf.code'] = false

        return result
      },
      message: SAISIE_OBLIGATOIRE,
      level: LevelRegleEnum.Transmettre,
    },

    controleFinancementObligatoire: {
      validateur: (model, _globalModel, context) => {
        const result: ModelValidateurResult<ContratProADTO> = {}

        if (context.status.financement?.$status === PropertyStatus.Required && isNullOrUndefined(model.financement))
          result['financement'] = false

        return result
      },
      message: SAISIE_OBLIGATOIRE,
      level: LevelRegleEnum.Transmettre,
    },
  },
} as const
